<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="700px"
    persistent
  >
    <v-card>
      <v-card-title class="headline primary white--text">
        <v-icon left dark>mdi-plus-box-multiple</v-icon>
        Luo tai tuo budjetti
      </v-card-title>

      <v-card-text class="pt-5">
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.number="selectedYear"
              label="Budjettivuosi *"
              type="number"
              outlined
              dense
              min="2020"
              max="2040"
              :rules="[(v) => !!v || 'Vuosi vaaditaan']"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="title"
              label="Budjetin nimi / otsikko"
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <div class="text-subtitle-1 font-weight-bold mb-2">
          Valitse luontitapa:
        </div>

        <v-radio-group v-model="createMode" column>
          <v-radio
            v-if="selectedYear === 2026"
            value="seed2026"
            label="Alusta vuoden 2026 valmiista talousarviosta (1-Budjetti 2026.xlsx)"
          ></v-radio>
          <v-radio
            value="excel"
            label="Lataa uusi Excel-budjettipohja (.xlsx)"
          ></v-radio>
          <v-radio
            value="scratch"
            label="Luo tyhjä budjetti (alustettuna 7 kustannuspaikalla ilman Exceliä)"
          ></v-radio>
          <v-radio
            v-if="availableYears.length > 0"
            value="copy"
            :label="`Kopioi tilit olemassa olevasta vuodesta (${availableYears.join(
              ', ',
            )})`"
          ></v-radio>
        </v-radio-group>

        <!-- Mode: Excel File Upload -->
        <v-expand-transition>
          <div v-if="createMode === 'excel'" class="mt-2 mb-4">
            <v-file-input
              v-model="excelFile"
              accept=".xlsx, .xls"
              label="Valitse Excel-tiedosto (.xlsx)"
              prepend-icon="mdi-file-excel"
              outlined
              dense
              show-size
              @change="handleExcelUpload"
            ></v-file-input>

            <v-alert v-if="excelError" type="error" dense outlined class="mt-2">
              {{ excelError }}
            </v-alert>
          </div>
        </v-expand-transition>

        <!-- Mode: Copy from another year -->
        <v-expand-transition>
          <div v-if="createMode === 'copy'" class="mt-2 mb-4">
            <v-select
              v-model="sourceYear"
              :items="availableYears"
              label="Lähdevuosi, josta tilit kopioidaan"
              outlined
              dense
            ></v-select>
          </div>
        </v-expand-transition>

        <!-- Preview of structure -->
        <v-card
          outlined
          class="pa-3 grey lighten-5 mt-3"
          v-if="previewProjects.length > 0"
        >
          <div class="font-weight-bold mb-2 d-flex align-center">
            <v-icon small left color="primary">mdi-format-list-bulleted</v-icon>
            Luotavat kustannuspaikat ({{ previewProjects.length }} kpl):
          </div>
          <v-chip
            v-for="p in previewProjects"
            :key="p.id"
            small
            class="mr-2 mb-2"
            color="primary"
            outlined
          >
            <strong>{{ p.code }}</strong
            >&nbsp;{{ p.name }} ({{ p.accounts ? p.accounts.length : 0 }} tiliä)
          </v-chip>
        </v-card>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)" :disabled="loading">
          Peruuta
        </v-btn>
        <v-btn
          color="primary"
          @click="submitCreate"
          :loading="loading"
          :disabled="!canSubmit"
        >
          Luo ja tallenna budjetti
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import defaultBudget2026 from "@/assets/defaultBudget2026.json";
import {
  parseBudgetExcel,
  createDefaultBudgetStructure,
} from "@/utils/budgetExcelParser";
import { saveBudget, subscribeBudget } from "@/services/budgetService";

export default {
  name: "BudgetCreateDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    currentYear: {
      type: Number,
      default: 2026,
    },
    availableYears: {
      type: Array,
      default: () => [2026],
    },
  },
  data() {
    return {
      selectedYear: this.currentYear,
      title: "",
      createMode: "seed2026",
      excelFile: null,
      excelError: "",
      excelParsedData: null,
      sourceYear: null,
      loading: false,
      previewProjects: [],
    };
  },
  computed: {
    canSubmit() {
      if (!this.selectedYear) return false;
      if (this.createMode === "excel" && !this.excelParsedData) return false;
      if (this.createMode === "copy" && !this.sourceYear) return false;
      return true;
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.selectedYear = this.currentYear;
        this.title = `NOORMARKUN VPK, TALOUSARVIO VUODELLE ${this.selectedYear}`;
        this.createMode = this.selectedYear === 2026 ? "seed2026" : "scratch";
        this.updatePreview();
      }
    },
    selectedYear(val) {
      this.title = `NOORMARKUN VPK, TALOUSARVIO VUODELLE ${val}`;
      if (val === 2026 && this.createMode === "scratch") {
        this.createMode = "seed2026";
      } else if (val !== 2026 && this.createMode === "seed2026") {
        this.createMode = "scratch";
      }
      this.updatePreview();
    },
    createMode() {
      this.updatePreview();
    },
  },
  methods: {
    async handleExcelUpload(file) {
      this.excelError = "";
      this.excelParsedData = null;
      if (!file) {
        this.updatePreview();
        return;
      }
      try {
        const parsed = await parseBudgetExcel(file, this.selectedYear);
        this.excelParsedData = parsed;
        if (parsed.year) this.selectedYear = parsed.year;
        this.previewProjects = parsed.projects;
      } catch (err) {
        this.excelError = `Excel-tiedoston lukeminen epäonnistui: ${err.message}`;
      }
    },
    updatePreview() {
      if (this.createMode === "seed2026") {
        this.previewProjects = defaultBudget2026.projects;
      } else if (this.createMode === "scratch") {
        const blank = createDefaultBudgetStructure(this.selectedYear);
        this.previewProjects = blank.projects;
      } else if (this.createMode === "excel" && this.excelParsedData) {
        this.previewProjects = this.excelParsedData.projects;
      } else if (this.createMode === "copy") {
        this.previewProjects = [];
      }
    },
    async submitCreate() {
      this.loading = true;
      try {
        let budgetPayload = null;

        if (this.createMode === "seed2026") {
          budgetPayload = {
            ...defaultBudget2026,
            year: this.selectedYear,
            title: this.title,
            updatedAt: new Date().toISOString(),
          };
        } else if (this.createMode === "excel" && this.excelParsedData) {
          budgetPayload = {
            ...this.excelParsedData,
            year: this.selectedYear,
            title: this.title,
            updatedAt: new Date().toISOString(),
          };
        } else if (this.createMode === "scratch") {
          budgetPayload = {
            ...createDefaultBudgetStructure(this.selectedYear),
            title: this.title,
            updatedAt: new Date().toISOString(),
          };
        } else if (this.createMode === "copy" && this.sourceYear) {
          // Fetch source budget to copy
          budgetPayload = await new Promise((resolve) => {
            const unsub = subscribeBudget(this.sourceYear, (source) => {
              unsub();
              if (source) {
                // Clone accounts, resetting actuals
                const clonedProjects = (source.projects || []).map((p) => ({
                  ...p,
                  accounts: (p.accounts || []).map((a) => ({
                    ...a,
                    baseToteutuma: 0,
                  })),
                }));
                resolve({
                  year: this.selectedYear,
                  title: this.title,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  source: `copy_${this.sourceYear}`,
                  projects: clonedProjects,
                });
              } else {
                resolve(null);
              }
            });
          });
        }

        let actionDesc = "Budjetti alustettu";
        if (this.createMode === "template") {
          actionDesc = "Budjetti alustettu vuoden 2026 talousarviopohjasta";
        } else if (this.createMode === "upload") {
          actionDesc = `Budjetti tuotu Excel-tiedostosta (${
            this.file ? this.file.name : "Excel"
          })`;
        } else if (this.createMode === "scratch") {
          actionDesc = `Budjetti luotu tyhjästä ilman pohjaa vuodelle ${this.selectedYear}`;
        } else if (this.createMode === "copy") {
          actionDesc = `Budjetti kopioitu edelliseltä vuodelta ${this.sourceYear}`;
        }

        const historyMeta = {
          actionType: "CREATE",
          summary: actionDesc,
          details: `Lähde: ${budgetPayload.source || this.createMode}`,
        };

        await saveBudget(this.selectedYear, budgetPayload, historyMeta);
        this.$toast?.success?.(
          `Vuoden ${this.selectedYear} budjetti tallennettu onnistuneesti!`,
        );
        this.$emit("created", this.selectedYear);
        this.$emit("input", false);
      } catch (err) {
        console.error("Error creating budget:", err);
        this.$toast?.error?.(err.message || "Budjetin luonti epäonnistui");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
